import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Gift, Lock, Sparkles, Star } from 'lucide-react';
import API_URL from '@/config/api';

const gbp = (n) => `£${Number(n || 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;
const title = (name) => { const n = name.replace(/^£[\d,.]+\s*off\s*(a |an |any )?/i, ''); return n ? n.charAt(0).toUpperCase() + n.slice(1) : name; };

/** One voucher as a tear-off ticket. `state`: ready | selected | progress | locked | other */
function Ticket({ v, selected, locked, onToggle, accent }) {
  const ready = v.applicable && !locked;
  const pct = v.unit === 'm2' || v.need > 1 ? Math.min(100, Math.round((v.have / v.need) * 100)) : v.have > 0 ? 100 : 0;
  const dim = locked || !v.relevant;
  return (
    <button type="button" onClick={ready ? onToggle : undefined} aria-pressed={selected} disabled={!ready}
      className={`group relative w-full text-left rounded-xl overflow-hidden transition-transform duration-200 ${ready ? 'hover:-translate-y-0.5 cursor-pointer' : 'cursor-default'} ${dim ? 'opacity-60' : ''}`}
      style={{ background: selected ? accent : '#27292E', boxShadow: selected ? `0 8px 24px -10px ${accent}` : '0 6px 18px -12px rgba(0,0,0,.6)' }}
      data-testid={`voucher-ticket-${v.id}`} data-state={selected ? 'selected' : ready ? 'ready' : locked ? 'locked' : 'progress'}>
      <div className="flex items-stretch">
        <div className="relative flex flex-col items-center justify-center px-3 py-3 min-w-[78px]" style={{ background: selected ? 'rgba(0,0,0,.18)' : 'rgba(255,255,255,.06)' }}>
          <span className="text-[9px] uppercase tracking-[0.18em] font-bold" style={{ color: selected ? '#fff' : accent }}>Voucher</span>
          <span className="text-2xl font-extrabold leading-none mt-0.5 text-white">{gbp(v.value)}</span>
          <span className="absolute -right-2 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-white/95" aria-hidden="true" />
        </div>
        <div className="flex-1 min-w-0 px-3.5 py-2.5 border-l border-dashed border-white/25">
          <div className="flex items-start justify-between gap-2">
            <div className="text-sm font-bold text-white leading-snug">{title(v.name)}</div>
            {selected ? <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-white text-[#27292E] rounded-full px-2 py-0.5" data-testid={`voucher-applied-${v.id}`}><Check className="w-3 h-3" /> Applied</span>
              : ready ? <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 text-[#27292E]" style={{ background: accent }}><Sparkles className="w-3 h-3" /> Tap to apply</span>
                : locked ? <Lock className="w-3.5 h-3.5 text-white/60 shrink-0" /> : null}
          </div>
          {locked ? <div className="text-[11px] text-white/70 mt-1">Leave a Google review to unlock</div>
            : <>
              <div className="text-[11px] mt-1" style={{ color: selected ? 'rgba(255,255,255,.9)' : ready ? '#86efac' : 'rgba(255,255,255,.75)' }} data-testid={`voucher-hint-${v.id}`}>{selected ? `${gbp(v.value)} taken off this order` : v.hint}</div>
              {v.relevant && !ready && (v.need > 1) && (
                <div className="mt-1.5 h-1.5 rounded-full bg-white/15 overflow-hidden"><div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${pct}%`, background: accent }} /></div>
              )}
            </>}
        </div>
      </div>
    </button>
  );
}

/**
 * Live voucher rail for the order builders. Re-evaluates the basket against the trainee's unlocked vouchers;
 * one voucher per order. Renders nothing for users without a voucher claim.
 * items: [{name, qty}] · brand: probound|proepoxy|prodecor · system: metallic|high_build (epoxy) · areaM2: total m²
 */
export default function VoucherRail({ brand, system, areaM2 = 0, items = [], selectedId, onSelect, accent = '#F5B301', compact = false }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const timer = useRef(null);
  const key = useMemo(() => JSON.stringify({ system, areaM2, items: items.map((i) => [i.name, i.qty]) }), [system, areaM2, items]);

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      fetch(`${API_URL}/api/vouchers/evaluate`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ brand, system, area_m2: Number(areaM2) || 0, items }) })
        .then((r) => (r.ok ? r.json() : null)).then((d) => d && setData(d)).catch(() => {});
    }, 350);
    return () => clearTimeout(timer.current);
  }, [brand, key]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedId && data && !data.vouchers.some((v) => v.id === selectedId && v.applicable)) onSelect?.(null, 0);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data || !data.claim_status || data.vouchers.length === 0) return null;
  const locked = data.claim_status !== 'approved';
  const sorted = [...data.vouchers].sort((a, b) => (b.applicable - a.applicable) || (b.relevant - a.relevant) || (b.value - a.value));
  const readyCount = sorted.filter((v) => v.applicable).length;

  return (
    <section className="rounded-2xl p-3 sm:p-4 border" style={{ background: 'linear-gradient(160deg,#fffaf0,#fff3d6)', borderColor: `${accent}66` }} data-testid="voucher-rail">
      <header className="flex items-center justify-between gap-2 mb-2.5">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: accent }}><Gift className="w-4 h-4 text-[#27292E]" /></div>
          <div className="min-w-0">
            <div className="text-sm font-extrabold text-[#27292E] leading-tight">Your training vouchers</div>
            <div className="text-[11px] text-gray-600 leading-tight" data-testid="voucher-rail-summary">
              {locked ? 'Locked — leave us a Google review to unlock them' : selectedId ? 'One voucher per order · applied below' : readyCount ? `${readyCount} ready to use · one per order` : 'Add a little more to unlock one'}
            </div>
          </div>
        </div>
        {locked ? <button type="button" onClick={() => navigate('/vouchers')} className="shrink-0 inline-flex items-center gap-1 text-xs font-bold text-[#27292E] rounded-full px-3 py-1.5" style={{ background: accent }} data-testid="voucher-rail-unlock"><Star className="w-3.5 h-3.5" /> Unlock</button>
          : selectedId ? <button type="button" onClick={() => onSelect?.(null, 0)} className="shrink-0 text-xs font-semibold text-gray-600 hover:underline" data-testid="voucher-rail-clear">Remove</button> : null}
      </header>
      <div className={`grid gap-2 ${compact ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
        {(compact && !showAll ? sorted.filter((v, i) => v.applicable || v.id === selectedId || i < Math.max(2, readyCount)) : sorted).map((v) => <Ticket key={v.id} v={v} locked={locked} selected={selectedId === v.id} accent={accent} onToggle={() => onSelect?.(selectedId === v.id ? null : v.id, selectedId === v.id ? 0 : v.value)} />)}
      </div>
      {compact && !showAll && sorted.length > Math.max(2, readyCount) && (
        <button type="button" onClick={() => setShowAll(true)} className="mt-2 w-full text-xs font-semibold text-gray-700 hover:underline" data-testid="voucher-rail-more">Show all {sorted.length} vouchers</button>
      )}
    </section>
  );
}
